// Ported from dev-scrips-pm-react src/billing/components/patient-balance-cards.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { ExternalLink } from 'lucide-react';

import { formatMoney } from '@/shared/lib/money';
/**
 * Patient financial stat cards atop the appointment Billing tab — Flutter
 * `PatientAvailableBalanceWidget`. Data comes straight off the
 * PreLoadAppointmentDetails response (`billingData.availableBalance.balance`),
 * so no extra fetch (verified curl 2026-06-04):
 *   balance[] = [{ subject: "Total Unpaid", amount, financialEventStatus: "negative" },
 *                { subject: "Available Balance", amount, financialEventStatus: "positive" }]
 *
 * `financialEventStatus` drives the accent: "negative" (owed) → red,
 * "positive" (credit) → brand. The Available-Balance card links out to the
 * patient's advance-receipts screen — Flutter
 * `billing_tab.dart:314-344` (`PatientAvailableBalanceWidget.onLinkClick` →
 * `Navigator.push(PatientAdvanceReceiptsScreen)`). Wire it via `onAvailableClick`.
 */
export interface PatientBalanceEntry {
  subject?: string;
  amount?: number;
  financialEventStatus?: string;
}

interface Props {
  balance: PatientBalanceEntry[];
  currency?: string;
  /**
   * Fires when the Available-Balance card's link icon is clicked — the host
   * opens the patient's advance-receipts view. Omit to keep the icon
   * decorative.
   */
  onAvailableClick?: () => void;
}

function fmt(n?: number): string {
  if (n == null) return '0.00';
  return formatMoney(n);
}

export function PatientBalanceCards({
  balance,
  currency = 'AED',
  onAvailableClick,
}: Props) {
  if (!balance || balance.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {balance.map((b, i) => {
        const isNegative =
          (b.financialEventStatus ?? '').toLowerCase() === 'negative';
        const accent = isNegative ? 'border-l-grey-400' : 'border-l-brand-primary';
        const amountTone = 'text-text-primary';
        const isAvailable = !isNegative;
        return (
          <div
            key={b.subject ?? i}
            className={`rounded-xl border border-border-default bg-surface-card border-l-4 ${accent} px-6 py-4`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium leading-4 text-text-secondary">
                {b.subject ?? '—'}
              </span>
              {isAvailable &&
                (onAvailableClick ? (
                  <button
                    type="button"
                    onClick={onAvailableClick}
                    aria-label="View advance receipts"
                    className="inline-flex text-brand-primary hover:text-interactive-hover cursor-pointer"
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  </button>
                ) : (
                  <ExternalLink
                    className="h-4 w-4 text-brand-primary"
                    aria-hidden
                  />
                ))}
            </div>
            <div className={`mt-1 text-size-lg font-semibold tabular-nums ${amountTone}`}>
              {currency} {fmt(b.amount)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
